import GeoJSON from 'ol/format/GeoJSON.js';
import { Fill, Style, Stroke, Text } from 'ol/style';
import { transform } from 'ol/proj';
import { Vector as VectorSource } from 'ol/source.js';
import { Vector as VectorLayer } from 'ol/layer.js';

// DB ve statik geojson köy isimlerini Türkçe karakter farklarından bağımsız
// karşılaştırmak için ortak forma indirger.
const turkishCharMap = {
  ç: 'c',
  Ç: 'c',
  ğ: 'g',
  Ğ: 'g',
  ı: 'i',
  İ: 'i',
  I: 'i',
  ö: 'o',
  Ö: 'o',
  ş: 's',
  Ş: 's',
  ü: 'u',
  Ü: 'u',
};

const normalizeTurkishText = (value) =>
  (value || '')
    .toString()
    .trim()
    .split('')
    .map((char) => (turkishCharMap[char] || char.toLowerCase()))
    .join('');

const geojsonToLayer = (geojsonObject, name) => {
  const normalizedName = normalizeTurkishText(name);

  const vectorSource = new VectorSource({
    features: new GeoJSON().readFeatures(geojsonObject).filter((feature) => {
      const properties = feature.getProperties();
      const text = properties.text;
      return normalizeTurkishText(text) === normalizedName;
    }),
  });

  const sourceProjection = 'EPSG:4326';
  const targetProjection = 'EPSG:32637';

  vectorSource.forEachFeature((feature) => {
    const geometry = feature.getGeometry();
    geometry.transform(sourceProjection, targetProjection);
  });

  const vectorLayer = new VectorLayer({
    source: vectorSource,
    title: 'kadastral',
    style: new Style({
      stroke: new Stroke({
        color: 'blue',
        width: 5,
      }),
      fill: new Fill({
        color: 'rgba(0, 0, 0, 0)',
      }),
    }),
  });

  const features = vectorLayer.getSource().getFeatures();
  const feature = features[0];

  if (!feature) {
    return { geometryId: null, vectorLayer: null };
  }

  const properties = feature.getProperties();
  const geometryId = properties.id;

  return { geometryId, vectorLayer };
};

const parcelGeojsonToLayer = (geojsonObject) => {
  const vectorSource = new VectorSource({
    features: new GeoJSON().readFeatures(geojsonObject),
  });

  const vectorLayer = new VectorLayer({
    source: vectorSource,
    title: 'kadastral',
    style: new Style({
      stroke: new Stroke({
        color: 'blue',
        width: 5,
      }),
      fill: new Fill({
        color: 'rgba(0, 0, 0, 0)',
      }),
    }),
  });

  return vectorLayer;
};

const queryGeojsonToLayer = (geojsonObject, map) => {
  const vectorSource = new VectorSource({
    features: new GeoJSON().readFeatures(geojsonObject),
  });

  const vectorLayer = new VectorLayer({
    source: vectorSource,
    title: 'sorgu',
    // Parsel sınırı; ürün katmanının ve seçim vurgusunun üstünde kalması
    // için yüksek bir zIndex veriyoruz. Dolgu, altındaki ürünlerin rahat
    // görünebilmesi için %25 opaklıkta (yarı saydam) bırakılıyor.
    zIndex: 20,
    style: new Style({
      stroke: new Stroke({
        color: '#1976D2',
        width: 4,
      }),
      fill: new Fill({
        color: 'rgba(25, 118, 210, 0.25)',
      }),
    }),
  });

  removeQueryLayer(map);

  map.addLayer(vectorLayer);

  return vectorLayer;
};

const removeQueryLayer = (map) => {
  const layersToRemove = map
    .getLayers()
    .getArray()
    .filter((mapLayer) => {
      const properties = mapLayer.getProperties();
      return properties.title === 'sorgu';
    });
  layersToRemove.forEach((layer) => {
    map.removeLayer(layer);
  });
};

const queryGeojsonToLayerProduct = (urunlerGeoJson, map) => {
  const vectorSourceUrunler = new VectorSource({
    features: new GeoJSON().readFeatures(urunlerGeoJson),
  });

  const vectorLayerUrunler = new VectorLayer({
    source: vectorSourceUrunler,
    title: 'mahsul',
    // Parsel sınır katmanının altında kalmasını sağlamak için daha düşük
    // bir zIndex kullanıyoruz.
    zIndex: 10,
    style: function (feature) {
      const urun = feature.get('urun') || 'tanımsız';
      const alan = feature.get('alan');
      const productColors = {
        hububat: '#8ecd53',
        misir: '#f1c40f',
        mısır: '#f1c40f',
        pamuk: '#5dade2',
        tanımsız: '#9340dc',
      };
      const color = productColors[(urun || '').toString().toLowerCase()] || '#9340dc';

      const textStyle = new Text({
        text: `${urun}\n${parseFloat(alan).toFixed(2)}`,
        stroke: new Stroke({
          color: 'white',
          width: 1,
        }),
        scale: 1.5,
        overflow: true,
        offsetY: -20,
      });

      return new Style({
        text: textStyle,
        stroke: new Stroke({
          color,
          width: 3,
        }),
        fill: new Fill({
          color: `${color}26`,
        }),
      });
    },
  });

  removeQueryLayerProduct(map);

  map.addLayer(vectorLayerUrunler);

  return vectorLayerUrunler;
};

const removeQueryLayerProduct = (map) => {
  const layersToRemove = map
    .getLayers()
    .getArray()
    .filter((mapLayer) => {
      const properties = mapLayer.getProperties();
      return properties.title === 'mahsul';
    });
  layersToRemove.forEach((layer) => {
    map.removeLayer(layer);
  });
};

const EYLU_YESIL_COLOR = '#2E7D32';

const queryGeojsonToLayerEylulYesil = (eylulGeoJson, map) => {
  removeQueryLayerEylulYesil(map);

  const features = new GeoJSON().readFeatures(
    eylulGeoJson || { type: 'FeatureCollection', features: [] }
  );
  if (features.length === 0) {
    return null;
  }

  const vectorLayer = new VectorLayer({
    source: new VectorSource({ features }),
    title: 'eylul_yesil',
    // Ürün vurgusu (10) ile parsel sınırı (20) arasında kalır.
    zIndex: 9,
    style: function (feature) {
      const etiket = feature.get('etiket') || 'Eylül Yeşil';
      const alan = parseFloat(feature.get('alan'));
      const alanText = Number.isFinite(alan) ? alan.toFixed(2) : '';

      return new Style({
        text: new Text({
          text: alanText ? `${etiket}\n${alanText}` : etiket,
          stroke: new Stroke({
            color: 'white',
            width: 1,
          }),
          scale: 1.5,
          overflow: true,
          offsetY: 20,
        }),
        stroke: new Stroke({
          color: EYLU_YESIL_COLOR,
          width: 3,
        }),
        fill: new Fill({
          color: `${EYLU_YESIL_COLOR}26`,
        }),
      });
    },
  });

  map.addLayer(vectorLayer);

  return vectorLayer;
};

const removeQueryLayerEylulYesil = (map) => {
  const layersToRemove = map
    .getLayers()
    .getArray()
    .filter((mapLayer) => {
      const properties = mapLayer.getProperties();
      return properties.title === 'eylul_yesil';
    });
  layersToRemove.forEach((layer) => {
    map.removeLayer(layer);
  });
};

const geojsonParsetoText = (geojsonObject) => {
  const features = geojsonObject.features;
  const parsedData = features.map((feature) => feature.properties.text);
  return parsedData.sort();
};

const zoomToLayer = (map, vektorLayer) => {
  map.getView().fit(vektorLayer.getSource().getExtent());
};

const addLayerToMap = (layer, map) => {
  removeKadastralLayer(map);

  map.addLayer(layer);
};

const removeKadastralLayer = (map) => {
  const layersToRemove = map
    .getLayers()
    .getArray()
    .filter((mapLayer) => {
      const properties = mapLayer.getProperties();
      return properties.title === 'kadastral';
    });
  layersToRemove.forEach((layer) => {
    map.removeLayer(layer);
  });
};

export {
  geojsonToLayer,
  geojsonParsetoText,
  zoomToLayer,
  addLayerToMap,
  removeKadastralLayer,
  parcelGeojsonToLayer,
  queryGeojsonToLayer,
  removeQueryLayer,
  queryGeojsonToLayerProduct,
  removeQueryLayerProduct,
  queryGeojsonToLayerEylulYesil,
  removeQueryLayerEylulYesil,
  normalizeTurkishText,
};
